import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import RemindersView from './reminders-view';
import {
  statusFilterOptions,
  StatusFilterValue,
  DEFAULT_ASSIGNEE,
  AssigneeOption,
} from './options';
import { useAuthStore } from '../../../store';
import ReminderEditModal from '../../../components/modals/reminder-edit-modal';
import ReminderRescheduleModal from '../../../components/modals/reminder-reschedule-modal';
import useRemindersSearch from '../../../hooks/use-reminders-search';
import useReminderActions from '../../../hooks/use-reminder-actions';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';

interface DefaultSearch {
  [key: string]: string | string[];
  assignee: string;
  status: string;
}

// Use "all" in URL to represent "ALL" (empty string) so it persists in the URL
const ALL_ASSIGNEE_URL_VALUE = 'all';

const defaultSearch: DefaultSearch = {
  assignee: '',
  status: 'pending',
};

const RemindersContainer = () => {
  const { loggedInTeamMember } = useAuthStore();
  const location = useLocation();
  const hasInitializedDefault = useRef(false);

  // URL-based filter state
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const { assignee: assigneeValue, status: statusValue } = queryValues;

  // Use the reminders search hook
  const {
    reminders, totalHits, isLoading, error, hasMore, loadMore, refetch,
  } = useRemindersSearch({
    assignedTo: assigneeValue === ALL_ASSIGNEE_URL_VALUE ? '' : assigneeValue,
    statusFilter: (statusValue || defaultSearch.status) as StatusFilterValue,
  });

  // Use the reminder actions hook
  const {
    create,
    edit,
    duplicate,
    reschedule,
    toggleComplete,
    dismiss,
    reschedulingId,
    togglingCompleteId,
    editModalProps,
    rescheduleModalProps,
    assignees,
  } = useReminderActions({ refetch });

  // Convert URL assignee value to AssigneeOption object
  const selectedAssignee = useMemo(() => {
    const normalizedValue = assigneeValue === ALL_ASSIGNEE_URL_VALUE ? '' : assigneeValue;
    if (!normalizedValue) return DEFAULT_ASSIGNEE;
    return assignees.find((tm) => tm.value === normalizedValue) || DEFAULT_ASSIGNEE;
  }, [assigneeValue, assignees]);

  // Convert URL status value to StatusFilterValue
  const selectedStatus = useMemo(
    () => (statusValue || defaultSearch.status) as StatusFilterValue,
    [statusValue],
  );

  // Store initial URL state to check if assignee was present on mount
  const initialAssigneeInURL = useRef<boolean | null>(null);
  if (initialAssigneeInURL.current === null) {
    const initialSearchParams = new URLSearchParams(location.search);
    // Check if assignee exists in URL (including "all" for "ALL" selection)
    initialAssigneeInURL.current = initialSearchParams.has('assignee');
  }

  // Set default assignee to logged-in user on initial mount if no assignee in URL
  // This only runs once after team members are loaded to avoid overriding user's "ALL" selection
  useEffect(() => {
    if (hasInitializedDefault.current) return;
    if (!initialAssigneeInURL.current && loggedInTeamMember && assignees.length > 0) {
      const userOption = assignees.find(
        (tm) => tm.value === loggedInTeamMember.id,
      );
      if (userOption) {
        hasInitializedDefault.current = true;
        updateURLQuery({ assignee: userOption.value });
      }
    } else if (initialAssigneeInURL.current || assignees.length > 0) {
      hasInitializedDefault.current = true;
    }
  }, [loggedInTeamMember, assignees, updateURLQuery]);

  // Filter handlers
  const onAssigneeChange = (assignee: AssigneeOption | null) => {
    const urlValue = !assignee || assignee.value === ''
      ? ALL_ASSIGNEE_URL_VALUE
      : assignee.value;
    updateURLQuery({ assignee: urlValue });
  };

  const onStatusChange = (status: string) => {
    updateURLQuery({ status });
  };

  return (
    <>
      <RemindersView
        // Data
        tickets={reminders}
        totalHits={totalHits}
        // Loading State
        isLoading={isLoading}
        error={error || undefined}
        hasMore={hasMore}
        loadMore={loadMore}
        // Assignee Filter
        assignees={assignees as AssigneeOption[]}
        selectedAssignee={selectedAssignee}
        onAssigneeChange={onAssigneeChange}
        // Status Filter
        statusOptions={statusFilterOptions}
        selectedStatus={selectedStatus}
        onStatusChange={onStatusChange}
        // Actions
        onNewReminder={create}
        onEditTicket={edit}
        onDuplicateTicket={duplicate}
        onRescheduleTicket={reschedule}
        onToggleComplete={toggleComplete}
        onDismissTicket={dismiss}
        // Action loading states
        reschedulingTicketId={reschedulingId}
        togglingCompleteTicketId={togglingCompleteId}
      />

      {/* Edit/Create Modal */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ReminderEditModal {...editModalProps} />

      {/* Reschedule Modal */}
      <ReminderRescheduleModal
        open={rescheduleModalProps.open}
        onClose={rescheduleModalProps.onClose}
        onSave={rescheduleModalProps.onSave}
        currentDueAt={rescheduleModalProps.currentDueAt}
      />
    </>
  );
};

export default RemindersContainer;
